import {API_URL} from '@services/api.config';
import {appendUploadAccessToken} from './uploadAuthUrl';

const IMAGE_EXT = /\.(jpe?g|png|webp|gif|svg)$/i;

export const pickProfileSignatureRaw = source =>
    source?.firma_url ??
    source?.firmaUrl ??
    source?.firma_perfil_url ??
    source?.signature_url ??
    source?.signatureUrl ??
    null;

const getFrontOrigin = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.location.origin;
};

const getApiHostOrigin = () => {
    const apiBase = String(API_URL || '').trim();

    if (!apiBase) {
        return getFrontOrigin();
    }

    if (apiBase.startsWith('/')) {
        const backendOrigin = process.env.REACT_APP_BACKEND_ORIGIN;

        if (backendOrigin) {
            try {
                return new URL(backendOrigin).origin;
            } catch (error) {
                // seguir
            }
        }

        return getFrontOrigin();
    }

    try {
        const apiUrl = new URL(apiBase, getFrontOrigin());
        return `${apiUrl.protocol}//${apiUrl.host}`;
    } catch (error) {
        return getFrontOrigin();
    }
};

/** Rutas relativas bajo /uploads/profile_signatures/ */
export const normalizeSignatureUploadsPath = rawUrl => {
    if (rawUrl == null || rawUrl === '') {
        return null;
    }

    let normalized = String(rawUrl).trim().replace(/\\/g, '/');

    if (!normalized || normalized === 'null' || normalized === 'undefined') {
        return null;
    }

    try {
        if (/^https?:\/\//i.test(normalized) || normalized.startsWith('//')) {
            const parsed = new URL(
                normalized.startsWith('//') ? `${window.location.protocol}${normalized}` : normalized
            );
            normalized = parsed.pathname;
        }
    } catch (error) {
        // seguir
    }

    if (!normalized.startsWith('/')) {
        normalized = `/${normalized}`;
    }

    normalized = normalized.replace(/^\/api(?=\/uploads\/)/, '');

    if (normalized.startsWith('/uploads/')) {
        return normalized.split('?')[0];
    }

    if (normalized.startsWith('/profile_signatures/')) {
        return `/uploads${normalized}`.split('?')[0];
    }

    const signatureIdx = normalized.indexOf('/profile_signatures/');

    if (signatureIdx >= 0) {
        const tail = normalized.slice(signatureIdx);

        return tail.startsWith('/uploads')
            ? tail.split('?')[0]
            : `/uploads${tail}`.split('?')[0];
    }

    if (normalized.startsWith('profile_signatures/')) {
        return `/uploads/${normalized}`.split('?')[0];
    }

    const fileName = normalized.replace(/^\//, '').split('/').pop();

    if (fileName && IMAGE_EXT.test(fileName)) {
        return `/uploads/profile_signatures/${fileName}`;
    }

    return null;
};

export const getProfileSignatureOrigins = () => [...new Set([getFrontOrigin(), getApiHostOrigin()].filter(Boolean))];

export const getProfileSignaturePathVariants = rawUrl => {
    const canonical = normalizeSignatureUploadsPath(rawUrl);

    if (!canonical) {
        return [];
    }

    const variants = new Set([canonical]);

    if (canonical.startsWith('/uploads/')) {
        variants.add(canonical.replace(/^\/uploads/, '/api/uploads'));
    }

    return [...variants];
};

export const buildProfileSignatureCandidates = (rawUrl, {bust = false} = {}) => {
    if (!rawUrl) {
        return [];
    }

    const trimmed = String(rawUrl).trim();

    if (/^blob:/i.test(trimmed) || /^data:image\//i.test(trimmed)) {
        return [trimmed];
    }

    const pathVariants = getProfileSignaturePathVariants(trimmed);

    if (!pathVariants.length) {
        return [];
    }

    const bustParam = bust ? `v=${Date.now()}` : '';
    const urls = [];

    getProfileSignatureOrigins().forEach(origin => {
        pathVariants.forEach(path => {
            let url = `${origin}${path}`;
            if (bustParam) {
                url += (url.includes('?') ? '&' : '?') + bustParam;
            }
            urls.push(appendUploadAccessToken(url));
        });
    });

    return [...new Set(urls)];
};

export const resolveProfileSignatureUrl = (rawUrl, options = {}) => {
    const candidates = buildProfileSignatureCandidates(rawUrl, options);

    return candidates[0] || null;
};
